/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { getLocations, getPlaylists } from '../api';
import { supabase } from '../supabase';

const BACKEND = process.env.REACT_APP_API_BASE;

export default function Activate() {
  const [locations, setLocations] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [tokenInput, setTokenInput] = useState('');
  const [name, setName] = useState('');
  const [locationId, setLocationId] = useState('');
  const [playlistId, setPlaylistId] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [quota, setQuota] = useState(null); // { quota, used, remaining, isAdmin }
  const [addonCount, setAddonCount] = useState(1);
  const [addonBilling, setAddonBilling] = useState('yearly');
  const [addonLoading, setAddonLoading] = useState(false);

  const loadQuota = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND}/quota`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) setQuota(await res.json());
    } catch (e) { /* ignore */ }
  };

  // Read token from URL if redirected from QR scan
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (t) setTokenInput(t);
    getLocations().then(r => setLocations(r.data));
    getPlaylists().then(r => setPlaylists(r.data));
    loadQuota();
  }, []);

  const quotaFull = quota && !quota.isAdmin && quota.remaining <= 0;

  const handleActivate = async () => {
    if (!tokenInput || !name || !locationId) {
      setError('Screen name, location and token are required.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND}/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          device_token: tokenInput,
          name,
          location_id: locationId,
          playlist_id: playlistId || null
        })
      });
      const data = await res.json();
      if (res.status === 402 || data.error === 'quota_exceeded') {
        setError(data.message || 'You have used all your screens. Please add more to your plan.');
        await loadQuota();
        setLoading(false);
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Activation failed');
      setSuccess(`Screen "${name}" activated successfully!`);
      setTokenInput('');
      setName('');
      setLocationId('');
      setPlaylistId('');
      await loadQuota();
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const handleAddonCheckout = async () => {
    setAddonLoading(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ screen_count: addonCount, billing: addonBilling, addon: true })
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else setError(data.error || 'Checkout could not be started');
    } catch (e) {
      setError(e.message);
    }
    setAddonLoading(false);
  };

  return (
    <div style={{ padding: 16, maxWidth: 540 }}>
      <h2 style={{ fontSize: 18, marginBottom: 6, fontFamily: 'Helvetica Neue, Arial, sans-serif' }}>Activate Screen</h2>
      <p style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>
        Enter the token shown on the TV screen, or scan the QR code with your phone to auto-fill it.
      </p>

      {/* Quota bar */}
      {quota && !quota.isAdmin && (
        <div style={{
          background: quotaFull ? '#FDE8E8' : '#F5EFE6',
          border: `1px solid ${quotaFull ? '#F5C6CB' : '#E0D8CF'}`,
          borderRadius: 10, padding: '12px 16px', marginBottom: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8
        }}>
          <div style={{ fontSize: 13, color: quotaFull ? '#9B2335' : '#555' }}>
            <strong>{quota.used}</strong> of <strong>{quota.quota}</strong> screens activated
            {quota.remaining > 0 && <span style={{ color: '#2D6A4F' }}> · {quota.remaining} remaining</span>}
          </div>
          {quotaFull && (
            <a href="#addon" onClick={(e) => { e.preventDefault(); document.getElementById('addon-box')?.scrollIntoView({ behavior: 'smooth' }); }} style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: '#8B1E2D', padding: '6px 14px', borderRadius: 8, textDecoration: 'none' }}>
              Add screens →
            </a>
          )}
        </div>
      )}

      {error && (
        <div style={{ background: '#FDE8E8', color: '#9B2335', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ background: '#E6F0EA', color: '#2D6A4F', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
          {success}
        </div>
      )}

      {quotaFull ? (
        <div id="addon-box" style={{ background: '#fff', border: '1px solid #E0D8CF', borderRadius: 12, padding: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 8 }}>All screens in use</div>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 20, lineHeight: 1.6 }}>
            You've activated all {quota.quota} of your screens. Add more to connect another screen — the extra screens are billed on top of your current plan.
          </div>

          {/* Add-on: how many + billing */}
          <div style={{ background: '#F5EFE6', borderRadius: 10, padding: '16px', marginBottom: 16, textAlign: 'left' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 12, textAlign: 'center' }}>Add screens</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 12 }}>
              <button onClick={() => setAddonCount(Math.max(1, addonCount - 1))} style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid #E0D8CF', background: '#fff', fontSize: 18, cursor: 'pointer' }}>−</button>
              <div style={{ fontSize: 28, fontWeight: 700, minWidth: 40 }}>{addonCount}</div>
              <button onClick={() => setAddonCount(Math.min(20, addonCount + 1))} style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid #E0D8CF', background: '#fff', fontSize: 18, cursor: 'pointer' }}>+</button>
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 10 }}>
              <button onClick={() => setAddonBilling('monthly')}
                style={{ padding: '5px 12px', borderRadius: 20, border: '1px solid #E0D8CF', cursor: 'pointer', fontSize: 11, fontWeight: addonBilling === 'monthly' ? 600 : 400, background: addonBilling === 'monthly' ? '#111' : '#fff', color: addonBilling === 'monthly' ? '#fff' : '#555' }}>
                Monthly
              </button>
              <button onClick={() => setAddonBilling('yearly')}
                style={{ padding: '5px 12px', borderRadius: 20, border: '1px solid #E0D8CF', cursor: 'pointer', fontSize: 11, fontWeight: addonBilling === 'yearly' ? 600 : 400, background: addonBilling === 'yearly' ? '#111' : '#fff', color: addonBilling === 'yearly' ? '#fff' : '#555' }}>
                Yearly
              </button>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#8B1E2D', textAlign: 'center' }}>
              +£{addonBilling === 'yearly' ? addonCount * 70 : addonCount * 7}/{addonBilling === 'yearly' ? 'year' : 'month'}
            </div>
          </div>

          <button onClick={handleAddonCheckout} disabled={addonLoading}
            style={{ width: '100%', background: '#8B1E2D', color: '#fff', borderRadius: 8, padding: '11px 24px', fontSize: 14, fontWeight: 700, textDecoration: 'none', border: 'none', cursor: addonLoading ? 'not-allowed' : 'pointer', opacity: addonLoading ? 0.7 : 1 }}>
            {addonLoading ? 'Redirecting to payment...' : 'Buy screens →'}
          </button>
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #E0D8CF', borderRadius: 12, padding: '1.5rem' }}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Device Token</label>
            <input
              value={tokenInput}
              onChange={e => setTokenInput(e.target.value)}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              style={inputStyle}
            />
            <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>Shown on TV screen when app first opens</div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Screen Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Main Display, Counter Screen"
              style={inputStyle}
            />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Location</label>
            <select value={locationId} onChange={e => setLocationId(e.target.value)} style={inputStyle}>
              <option value="">Select location</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Playlist <span style={{ color: '#999', fontWeight: 400 }}>(optional)</span></label>
            <select value={playlistId} onChange={e => setPlaylistId(e.target.value)} style={inputStyle}>
              <option value="">No playlist — assign later</option>
              {playlists.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <button
            onClick={handleActivate}
            disabled={loading}
            style={{
              width: '100%', padding: '11px', background: '#8B1E2D', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Activating...' : 'Activate Screen'}
          </button>
        </div>
      )}
    </div>
  );
}

const labelStyle = { fontSize: 12, color: '#555', display: 'block', marginBottom: 5, fontFamily: 'Helvetica Neue, Arial, sans-serif' };
const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #E0D8CF', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'Helvetica Neue, Arial, sans-serif' };